<script setup lang="ts">
import { computed } from 'vue'
import { RadioGroup, RadioGroupItem } from '@/components/shadcn'

export interface Option {
  value: string | number
  label: string
  disabled?: boolean
}

type StyleType =
  | 'default'   // 기본 스타일
  | 'primary'   // 강조 스타일 (파랑)
  | 'success'   // 성공 스타일 (초록)
  | 'danger'    // 위험 스타일 (빨강)
  | 'sm'        // 작은 크기
  | 'lg'        // 큰 크기
  | 'horizontal' // 가로 배치
  | 'vertical'  // 세로 배치

const props = withDefaults(
  defineProps<{
    modelValue?: string | number
    options?: Option[]
    disabled?: boolean
    required?: boolean
    name?: string
    id?: string
    class?: string
    /** 스타일 프리셋 */
    styletype?: StyleType
  }>(),
  {
    options: () => [],
    disabled: false,
    required: false,
    styletype: 'default',
  },
)

const emit = defineEmits<{
  'update:modelValue': [value: string | number]
  'change': [value: string | number]
}>()

/**
 * styletype -> class 매핑
 */
const STYLE_PRESETS: Record<StyleType, { groupClass: string; itemClass: string }> = {
  default: { 
    groupClass: '',
    itemClass: '',
  },
  primary: { 
    groupClass: '',
    itemClass: 'border-blue-500 text-blue-500',
  },
  success: { 
    groupClass: '',
    itemClass: 'border-green-500 text-green-600',
  },
  danger: { 
    groupClass: '',
    itemClass: 'border-destructive text-destructive',
  },
  sm: { 
    groupClass: '',
    itemClass: 'h-3.5 w-3.5',
  },
  lg: { 
    groupClass: '',
    itemClass: 'h-5 w-5',
  },
  horizontal: { 
    groupClass: 'flex flex-row gap-4',
    itemClass: '',
  },
  vertical: { 
    groupClass: 'grid gap-2',
    itemClass: '',
  },
}

const groupClass = computed(() => {
  const styleKey = props.styletype || 'default'
  const preset = STYLE_PRESETS[styleKey] ?? STYLE_PRESETS.default
  return [preset?.groupClass, props.class].filter(Boolean).join(' ')
})

const itemClass = computed(() => {
  const styleKey = props.styletype || 'default'
  const preset = STYLE_PRESETS[styleKey] ?? STYLE_PRESETS.default
  return preset?.itemClass
})

const handleChange = (value: string) => {
  emit('update:modelValue', value)
  emit('change', value)
}
</script>

<template>
  <RadioGroup 
    :id="id" 
    :model-value="String(modelValue)"
    :disabled="disabled"
    :required="required"
    :name="name"
    :class="groupClass" 
    @update:model-value="handleChange"
  >
    <div v-for="option in options" :key="option.value" :class="styletype === 'vertical' ? 'block' : 'flex items-center space-x-2'">
      <RadioGroupItem 
        :id="String(option.value)" 
        :value="String(option.value)" 
        :disabled="option.disabled"
        :class="itemClass"
      />
      <label
        :for="String(option.value)"
        class="text-xs font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
      >
        {{ option.label }}
      </label>
    </div>
  </RadioGroup>
</template>
